import type { FragmentType } from '@packages/gql/generated/shopping';
import { useAbTesting } from '@packages/abtesting';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type { GtmProduct } from '@packages/tracking';
import { PageMetaData } from '@packages/tracking';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useAddLastSeen } from '../../hooks';
import { getCategoryData } from './helper/getCategoryData';
import { useDefaultTracking } from './hooks/useDefaultTracking';

const TrackingFragment = graphql(/* GraphQL */ `
  fragment TrackingFragment on Product {
    akl
    sku
    orderNumber
    ...GetSizeAndVariantFromDimensionsFragment
    availability {
      ...GetDeterminedAvailabilityFragment
    }
    ...GtmProductViewFragment
    ...GetGlycerinDisplayProductPayloadFragment
  }
`);

export const TrackingBreadcrumbsFragment = graphql(/* GraphQL */ `
  fragment TrackingBreadcrumbsFragment on Breadcrumb {
    ...GetCategoryDataFragment
  }
`);

export type TrackingProps = {
  breadCrumbData: FragmentType<typeof TrackingBreadcrumbsFragment> | undefined | null;
  productData: FragmentType<typeof TrackingFragment>;
  navq?: GtmProduct['category'];
};

/**
 * Tracking component used to track
 * */
export const Tracking: FC<TrackingProps> = ({ navq, productData, breadCrumbData }) => {
  const { setDefaultViewOutcome } = useAbTesting();

  const unmaskedData = getFragmentData(TrackingFragment, productData);

  const { akl, orderNumber, sku } = unmaskedData;

  useAddLastSeen(orderNumber);

  const { categoryPath, categoryPathAsString } = getCategoryData(
    getFragmentData(TrackingBreadcrumbsFragment, breadCrumbData),
  );

  const { trackGtmProductView, trackGlycerinDisplayProduct, trackGtmPageNavigation } =
    useDefaultTracking();

  useEffect(() => {
    trackGtmProductView({ data: unmaskedData, navq, categoryPathAsString });
    // Glycerin Stuff
    // product event for glycerin tracking
    trackGlycerinDisplayProduct(unmaskedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku]);

  /* PDVPageNavigation event: new product loaded */
  useEffect(() => {
    if (akl) {
      trackGtmPageNavigation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akl]);

  // lackmus framework default view tracking for abtesting
  useEffect(() => {
    setDefaultViewOutcome(sku, akl, 'RPP');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [akl, sku]);

  return (
    <PageMetaData
      page={{ type: 'detailview', template: 'detailview' }}
      categoryPath={categoryPath}
    />
  );
};
